import { Text } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo, useState } from 'react';

import { TopbarButton } from 'app/views/layout/components/layoutContainer/components/topBar/components/button/topbarButton';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { TopbarButtonOld } from 'components/topbar/components/topbarButtonOld';
import { useCommandHandler } from 'utils/commands';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import Phone from '../containers/phone';
import SearchUser from '../containers/searchUser';
import { DispatchProps, StateProps } from '../containers/telephonyTopbarButton';
import { useSelectedFromNumberPresentation } from '../hooks/useSelectedFromNumberPresentation';

import { OutboundNumberSelector } from './OutboundNumberSelector';
import styles from './telephonyTopbarButton.module.scss';

type TelephonyTopbarButtonProps = StateProps & DispatchProps;

export const TelephonyTopbarButton = (
  props: TelephonyTopbarButtonProps,
): JSX.Element => {
  const flags = useFeatureFlag(['new-topbar']);

  const {
    activePhoneMetaConversation,
    canMakeOutBoundCall,
    isInternalCall,
    isResolvingDefaultNumber,
    isTelephonyDropdownOpen,
    phoneCallDisabledTooltipText,
    selectedFromNumber,
    telephonyCloseDropdown,
    telephonyOpenDropdown,
  } = props;
  const [isOwnNumbersViewOpen, setIsOwnNumbersViewOpen] = useState(false);
  const { fromNumberFormat } =
    useSelectedFromNumberPresentation(selectedFromNumber);

  useCommandHandler({
    name: 'app:openDialer',
    actionFn: () => {
      handleToggle(!isTelephonyDropdownOpen);
    },
  });

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        telephonyOpenDropdown();
        ampli.topbarPhonesOpened();
      } else {
        telephonyCloseDropdown();
      }
    },
    [telephonyCloseDropdown, telephonyOpenDropdown],
  );

  const toggleOwnNumbersView = useCallback(
    (isOwnNumbersViewOpen: boolean) => {
      setIsOwnNumbersViewOpen(isOwnNumbersViewOpen);
    },
    [setIsOwnNumbersViewOpen],
  );

  const { phone, activeCall, showNotificationIndicator } = useMemo(() => {
    if (activePhoneMetaConversation !== null) {
      return {
        phone: (
          <li className={styles.phoneWrapper}>
            <Phone
              isInternalCall={isInternalCall}
              canMakeOutBoundCall={canMakeOutBoundCall}
              metaConversation={activePhoneMetaConversation}
            />
          </li>
        ),
        activeCall: (
          <li role="headline" className={styles.headline}>
            <Text.Heading>Phone</Text.Heading>
            <span className={styles.badge}>Active</span>
          </li>
        ),
        showNotificationIndicator: true,
      };
    }

    return {
      phone: null,
      activeCall: (
        <li role="headline" className={styles.headline}>
          <Text.Heading>Phone</Text.Heading>
        </li>
      ),
      showNotificationIndicator: false,
    };
  }, [activePhoneMetaConversation, canMakeOutBoundCall, isInternalCall]);

  const label = 'Phone dialer';

  const tooltip = (
    <CommandKeybindingsTooltipContent commandName="app:openDialer">
      {label}
    </CommandKeybindingsTooltipContent>
  );

  return (
    <Popover.Root open={isTelephonyDropdownOpen} onOpenChange={handleToggle}>
      <Popover.Trigger asChild>
        {flags.newTopbar ? (
          <TopbarButton
            icon="phone"
            label={label}
            tooltip={tooltip}
            notification={showNotificationIndicator ? 1 : undefined}
          />
        ) : (
          <TopbarButtonOld
            showNotificationIndicator={showNotificationIndicator}
            icon="phone"
            tooltip={tooltip}
          />
        )}
      </Popover.Trigger>

      <Popover.Content
        className={styles.popover}
        side="bottom"
        align={flags.newTopbar ? 'start' : 'end'}
        sideOffset={parseInt(sizes.spacer.values[100])}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ul className={styles.dropdownContent}>
          {activeCall}

          <li role="separator" className={styles.divider}></li>

          {phone}

          <OutboundNumberSelector
            isOwnNumbersViewOpen={isOwnNumbersViewOpen}
            toggleOwnNumbersView={toggleOwnNumbersView}
          />

          <li role="separator" className={styles.divider}></li>

          <SearchUser
            fromNumberFormat={fromNumberFormat}
            toggleOwnNumbersView={toggleOwnNumbersView}
            canMakeOutBoundCall={canMakeOutBoundCall}
            isResolvingDefaultNumber={isResolvingDefaultNumber}
            phoneCallDisabledTooltipText={phoneCallDisabledTooltipText}
            isOwnNumbersViewOpen={isOwnNumbersViewOpen}
          />
        </ul>
      </Popover.Content>
    </Popover.Root>
  );
};
